import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import Post from 'flarum/common/models/Post';
import Discussion from 'flarum/common/models/Discussion';
import NotificationModel from 'flarum/common/models/Notification';
import PostUser from 'flarum/forum/components/PostUser';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import CommentPost from 'flarum/forum/components/CommentPost';
import TerminalPost from 'flarum/forum/components/TerminalPost';
import Icon from 'flarum/common/components/Icon';
import humanTime from 'flarum/common/helpers/humanTime';
import { handleOf } from './handle';
import HandleAvatar from './HandleAvatar';
import nameHeading from './nameHeading';
import composer from './composer';
import replyPlaceholder from './replyPlaceholder';
import notifications from './notifications';
import reactions from './reactions';
import replies from './replies';
import flags from './flags';
import lookupControl from './lookupControl';
export { default as extend } from './extend';
/**
 * Task 5.4 -- show the handle instead of the username in the post stream.
 *
 * **Task 5.5 has since removed the real identities from the API**, so this is
 * no longer the only thing standing between a reader and a username. It was,
 * for one commit, and the comment is kept because the ordering it describes is
 * what made 5.5 safe.
 *
 * WHY BOTH RENDER PATHS ARE COVERED. `PostUser` branches on `post.user()`:
 * `userViewItems` when the author is present, `noUserViewItems` when it is not.
 * Before 5.5 almost everything took the first branch; **since 5.5 hid the user
 * relationship, everything takes the second.** Covering only the branch that
 * ran at the time would have meant the forum silently reverting to "[Deleted]"
 * on every post the moment the API was secured. Covering both is why 5.5
 * changed nothing anyone could see.
 *
 * The guarantee itself now lives in the suite, in
 * `PayloadHandleTest::an_anonymous_caller_receives_no_user_resource_at_all`.
 */
app.initializers.add('ideasynq-handles', () => {
  Post.prototype.handle = Model.attribute('handle');
  Post.prototype.hasLiked = Model.attribute('hasLiked');
  Discussion.prototype.handle = Model.attribute('handle');
  Discussion.prototype.lastPostedHandle = Model.attribute('lastPostedHandle');
  Discussion.prototype.myHandle = Model.attribute('myHandle');
  NotificationModel.prototype.fromHandle = Model.attribute('fromHandle');

  /*
   * The discussion list subtitle -- "chip replied a day ago" on the forum's
   * front page, which is the first thing anybody sees.
   *
   * The whole view is replaced rather than extended because TerminalPost builds
   * its vnode in one expression, so there is nothing to reach into afterwards.
   * The structure below deliberately mirrors core's, including the same
   * translation keys, so the only difference is WHICH NAME goes in.
   *
   * Passing `username` alongside `user` is what does it: Translator's
   * preprocessParameters() fills `username` from the user model ONLY if it was
   * not supplied, so supplying it wins without having to remove the user.
   */
  override(TerminalPost.prototype, 'view', function () {
    // TerminalPost is one of core's remaining .js components, so its typings
    // describe attrs only as ComponentAttrs. Narrowing here keeps the cast in
    // one place instead of at every use below.
    const {
      discussion,
      lastPost
    } = this.attrs;
    const isLastPost = lastPost && discussion.replyCount();
    const handle = isLastPost ? discussion.lastPostedHandle?.() : discussion.handle?.();
    const time = isLastPost ? discussion.lastPostedAt() : discussion.createdAt();
    return m("span", null, !!isLastPost && m(Icon, {
      name: "fas fa-reply"
    }), ' ', app.translator.trans('core.forum.discussion_list.' + (isLastPost ? 'replied' : 'started') + '_text', {
      username: handle,
      // Core passes this through unguarded; its own typings say the date
      // may be absent, so narrow rather than inherit the looseness.
      ago: time ? humanTime(time) : ''
    }));
  });
  extend(PostUser.prototype, 'userViewItems', function (items, user, post) {
    const handle = handleOf(post);
    if (handle === null) return;

    /*
     * setContent rather than remove-and-add, so the item keeps its priority and
     * this stays a substitution rather than a reordering of the header.
     *
     * THE PROFILE LINK GOES WITH IT. Flarum wraps the name in a Link to
     * /u/{slug}, which is a real identity in the markup and a route to a page
     * listing everything that person has written everywhere -- the cross-Issue
     * correlator Section 4.10 calls "preserved absolutely". A handle is never a
     * real identity (DisplayIdentity::isRealIdentity() is false for every value
     * this system can currently produce), so nothing here may be linked.
     */
    items.setContent('postUser-name', nameHeading(handle, isSelf(post)));
  });
  extend(PostUser.prototype, 'noUserViewItems', function (items, user, post) {
    const handle = handleOf(post);
    if (handle === null) return;
    items.setContent('postUser-name', nameHeading(handle, isSelf(post)));
  });

  /*
   * The post stream's SIDE avatar -- a second, separate avatar from the one in
   * the post header, rendered by CommentPost rather than PostUser.
   *
   * Easy to miss: replacing the header's avatar left this one drawing Flarum's
   * empty fallback circle on every post, and it only turned up by counting
   * `.Avatar` elements that were not ours. Core also wraps it in a Link to
   * /u/{slug} whenever a user is present, which is the same profile link the
   * header dropped at 5.4.
   *
   * The original is called only to keep the no-handle case behaving exactly as
   * Flarum intends, rather than guessing at a placeholder.
   */
  override(CommentPost.prototype, 'avatar', function (original) {
    const post = this.attrs.post;
    const handle = handleOf(post);
    if (handle === null) return original();
    return HandleAvatar(handle, {
      className: 'Post-avatar',
      self: isSelf(post)
    });
  });

  /*
   * The discussion list's author avatar -- the forum's front page.
   *
   * DiscussionListItem builds it from `discussion.user()`, which Task 5.5 hid,
   * so today it draws Flarum's empty fallback circle: a blank grey disc on
   * every row. It also wraps that circle in a Tooltip reading "[deleted]
   * started N ago" and, when a user IS present, in a Link to /u/{slug}.
   *
   * Replaced whole rather than patched, because all three of those --
   * the image, the tooltip text and the profile link -- come from the User.
   */
  extend(DiscussionListItem.prototype, 'authorItems', function (items) {
    const discussion = this.attrs.discussion;
    const handle = handleOf(discussion);
    if (handle === null || !items.has('avatar')) return;
    items.setContent('avatar', m("span", {
      className: "DiscussionListItem-author-avatar"
    }, HandleAvatar(handle, {
      self: isOwnHandle(discussion, handle)
    })));
  });

  /*
   * Tasks 4.1 and 4.2 -- the composers and the reply placeholder.
   *
   * LAST IN THIS INITIALIZER ON PURPOSE. An exception anywhere in an
   * initializer aborts every registration after it, and at 4.2 one did: a
   * lazily-loaded component imported statically threw at boot, and every
   * Phase 5 substitution above would have been skipped had these come first --
   * as they briefly did. They only change what the VIEWER sees of themselves,
   * so they go after the code that governs how everybody else is shown.
   * Not otherwise ordering-sensitive: no enabled extension extends these
   * components.
   */
  composer();
  replyPlaceholder();
});

/**
 * Task 5.5, and it runs LAST on purpose.
 *
 * `reactions()` and `replies()` both rebuild items that flarum/likes and
 * flarum/mentions add to `CommentPost`'s action and footer lists. An
 * `ItemList` item can only be replaced once it exists, and it exists only if
 * the extension that adds it registered its own `extend` first -- so this is
 * ordering-sensitive in a way the rest of this file is not.
 *
 * `app.initializers` is itself an ItemList, and **higher priority runs first**.
 * A negative priority therefore puts this after every extension that did not
 * ask for one, which is all of them. Found the hard way: with these registered
 * in the main initializer above, the "replied to this" footer kept core's
 * broken version and silently read "[deleted] replied to this".
 *
 * The alternative was declaring flarum/mentions in composer.json so Flarum
 * boots us afterwards -- which is what Task 3.8 did for flarum/likes. That is
 * still the right move if this ever needs to be more than ordering, but a
 * priority is cheaper and does not change what the extension depends on.
 */
app.initializers.add('ideasynq-handles-late', () => {
  reactions();
  replies();
  notifications();
  // Task 6.3a. Late for the same reason: `flagReason` exists on Post only
  // once flarum-flags' own initializer has added it.
  flags();
  // Task 6.7. Last of all: it only ever ADDS a menu item, so nothing else
  // depends on it, and a throw here can take nothing else down with it.
  lookupControl();
}, -100);

/**
 * Is this the viewer's own contribution?
 *
 * Compares the post's handle with the viewer's own handle **in the same
 * Issue**, which the discussion carries as `myHandle` since 5.6. Before 5.5
 * this was `post.user() === app.session.user`; that relationship is gone, and
 * comparing handles is both the replacement and a safer one -- neither side is
 * a real identity, and both are scoped to this Issue, so nothing here can
 * correlate across Issues even if it were wrong.
 *
 * False for a guest, for a post whose author cannot be resolved, and for anyone
 * who has not yet contributed here -- in each case `myHandle` is the unresolved
 * placeholder, which is deliberately excluded rather than matched.
 */
function isSelf(post) {
  const handle = handleOf(post);
  const discussion = post?.discussion?.();
  if (!handle || !discussion) return false;
  return isOwnHandle(discussion, handle);
}

/**
 * Does `handle` belong to the viewer, within this discussion's Issue?
 *
 * `myHandle` is null -- not a placeholder -- when the viewer has no handle
 * here, so absence is the only case to exclude and no placeholder string has to
 * be duplicated in this language. See `HandleField::identityFor()`, which sends
 * null for exactly this reason.
 */
function isOwnHandle(discussion, handle) {
  const mine = discussion.myHandle?.();
  return Boolean(mine) && mine === handle;
}